import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  useTheme,
  Paper,
  Avatar,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  IconButton,
  Button,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  LinearProgress,
  alpha,
  Container,
  Tooltip,
  Badge,
  Stack,
} from "@mui/material";
import TopAppBar from "../../global/TopBar";
import SideBar from "../../global/SideBar";
import HeaderPage from "../../../components/Header";
import { tokens } from "../../../theme";
import { useAuth } from "../../../AuthContext";

// Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import CakeIcon from "@mui/icons-material/Cake";
import BadgeIcon from "@mui/icons-material/Badge";
import FlagIcon from "@mui/icons-material/Flag";
import FavoriteIcon from "@mui/icons-material/Favorite";
import VerifiedIcon from "@mui/icons-material/Verified";
import ScheduleIcon from "@mui/icons-material/Schedule";
import LoginIcon from "@mui/icons-material/Login";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MessageIcon from '@mui/icons-material/Message';
import TodayIcon from '@mui/icons-material/Today';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import TimelineIcon from '@mui/icons-material/Timeline';
import SecurityIcon from '@mui/icons-material/Security';
import PersonIcon from '@mui/icons-material/Person';

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`staff-tabpanel-${index}`}
      aria-labelledby={`staff-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const StaffProfile = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { id } = useParams();
  const navigate = useNavigate();
  const { getData, userRole } = useAuth();
  
  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [staffLeaves, setStaffLeaves] = useState([]);
  const [staffBorrows, setStaffBorrows] = useState([]);
  const [staffActivity, setStaffActivity] = useState([]);

  // Safe color access with fallbacks
  const safeColors = {
    primary: colors?.primary || { 500: '#1e1e1e', 400: '#2b2b2b', 300: '#7d7d7d', 100: '#e0e0e0' },
    blue: colors?.blue || { 500: '#308251', 400: '#266841' },
    green: colors?.green || { 500: '#198754', 400: '#146c43' },
    red: colors?.red || { 500: '#ff3903', 400: '#cc2e02' },
    orange: colors?.orange || { 500: '#ff7d03', 400: '#cc6402' },
    yellow: colors?.yellow || { 500: '#ffc107', 400: '#cc9a06' },
    teal: colors?.teal || { 500: '#0aa125', 400: '#08811e' },
    purple: colors?.indigo || { 500: '#0d6efd', 400: '#0a58ca' },
    gray: colors?.gray || { 500: '#adb5bd', 400: '#8a9197' },
    indigo: colors?.indigo || { 500: '#0d6efd', 400: '#0a58ca' },
    white: colors?.white || { 500: '#f0f1f7' },
    dark: colors?.dark || { 900: '#03050f' }
  };

  // Fetch staff data
  useEffect(() => {
    async function fetchStaffData() {
      setLoading(true);
      try {
        // Fetch staff details
        const staffResponse = await getData('proccess.php', 'staffs');
        if (staffResponse.success) {
          const foundStaff = staffResponse.data.find(s => s.Id === parseInt(id));
          setStaff(foundStaff || null);
        }

        // Fetch staff leaves
        const leavesResponse = await getData('proccess.php', 'leaves_V2');
        if (leavesResponse.success) {
          const staffLeaves = leavesResponse.data.filter(l => l.staff_id === parseInt(id));
          setStaffLeaves(staffLeaves);
        }

        // Fetch staff borrow records
        const borrowResponse = await getData('proccess.php', 'borrow_V2');
        if (borrowResponse.success) {
          const staffBorrows = borrowResponse.data.filter(b => b.staff_id === parseInt(id));
          setStaffBorrows(staffBorrows);
        }

        // Activity data
        setStaffActivity([
          { id: 1, action: 'Logged in', date: new Date(Date.now() - 3600000).toISOString(), type: 'auth' },
          { id: 2, action: 'Updated profile', date: new Date(Date.now() - 86400000).toISOString(), type: 'profile' },
          { id: 3, action: 'Requested leave', date: new Date(Date.now() - 172800000).toISOString(), type: 'leave' },
        ]);

      } catch (err) {
        console.error("Error fetching staff data:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchStaffData();
  }, [id, getData]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getInitials = () => {
    if (!staff) return '';
    return `${staff.FirstName?.[0] || ''}${staff.LastName?.[0] || ''}`;
  };

  const getStatusColor = () => {
    if (!staff) return safeColors.gray[500];
    if (staff.status === 'active' || staff.status === 1) return safeColors.green[500];
    return safeColors.red[500];
  };

  const getStatusText = () => {
    if (!staff) return 'Unknown';
    if (staff.status === 'active' || staff.status === 1) return 'Active';
    return 'Inactive';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', height: '100vh', bgcolor: safeColors.dark[900] }}>
        <SideBar />
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <TopAppBar />
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LinearProgress sx={{ width: '50%' }} />
          </Box>
        </Box>
      </Box>
    );
  }

  if (!staff) {
    return (
      <Box sx={{ display: 'flex', height: '100vh', bgcolor: safeColors.dark[900] }}>
        <SideBar />
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <TopAppBar />
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h4" color={safeColors.gray[500]}>
              Staff member not found
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: safeColors.dark[900] }}>
      <SideBar />
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
        <TopAppBar />
        
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Header with Back Button */}
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <IconButton 
                onClick={() => window.history.back()}
                sx={{ 
                  bgcolor: safeColors.primary[500],
                  '&:hover': { bgcolor: safeColors.primary[400] }
                }}
              >
                <ArrowBackIcon />
              </IconButton>
              <HeaderPage
                title="Staff Profile"
                subtitle={`Viewing details for ${staff.FirstName} ${staff.LastName}`}
              />
            </Box>

            {/* Profile Header Card */}
            <Paper 
              elevation={0}
              sx={{ 
                p: 3, 
                mb: 3,
                bgcolor: safeColors.primary[500],
                border: `1px solid ${safeColors.primary[400]}`,
                borderRadius: 3
              }}
            >
              <Grid container spacing={3} alignItems="center">
                <Grid size={{ xs: 12, md: 8 }}>
                  <Box display="flex" alignItems="center" gap={3}>
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      badgeContent={
                        <Box 
                          sx={{ 
                            width: 16, 
                            height: 16, 
                            borderRadius: '50%',
                            bgcolor: getStatusColor(),
                            border: `2px solid ${safeColors.white[500]}`
                          }} 
                        />
                      }
                    >
                      <Avatar 
                        src={staff.image ? `https://mindsinaction.com.na/assets/media/pictures/${staff.image}` : ''}
                        sx={{ 
                          width: 120, 
                          height: 120,
                          bgcolor: safeColors.primary[400],
                          border: `4px solid ${safeColors.blue[500]}`,
                          fontSize: '3rem'
                        }}
                      >
                        {getInitials()}
                      </Avatar>
                    </Badge>
                    
                    <Box>
                      <Box display="flex" alignItems="center" gap={2} mb={1}>
                        <Typography variant="h3" fontWeight="bold" color={safeColors.primary[100]}>
                          {staff.FirstName} {staff.LastName}
                        </Typography>
                        <Chip 
                          label={getStatusText()}
                          size="small"
                          sx={{ 
                            bgcolor: alpha(getStatusColor(), 0.1),
                            color: getStatusColor(),
                            fontWeight: 'bold'
                          }}
                        />
                      </Box>
                      
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <Chip 
                          icon={<WorkIcon />}
                          label={staff.role || 'Staff'}
                          size="small"
                          sx={{ 
                            bgcolor: alpha(safeColors.blue[500], 0.1),
                            color: safeColors.blue[500]
                          }}
                        />
                        <Chip 
                          icon={<SchoolIcon />}
                          label={staff.department || 'No Department'}
                          size="small"
                          sx={{ 
                            bgcolor: alpha(safeColors.green[500], 0.1),
                            color: safeColors.green[500]
                          }}
                        />
                        {staff.isVerified && (
                          <Chip 
                            icon={<VerifiedIcon />}
                            label="Verified"
                            size="small"
                            sx={{ 
                              bgcolor: alpha(safeColors.teal[500], 0.1),
                              color: safeColors.teal[500]
                            }}
                          />
                        )}
                      </Box>

                      <Typography variant="body1" color={safeColors.primary[100]}>
                        {staff.role_explanation || 'No role description provided'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Box display="flex" justifyContent="flex-end" gap={1}>
                    <Tooltip title="Send Message">
                      <IconButton 
                        sx={{ 
                          bgcolor: safeColors.blue[500],
                          color: safeColors.white[500],
                          '&:hover': { bgcolor: safeColors.blue[400] }
                        }}
                        onClick={()=>alert('Coming Soon !!!')}
                      >
                        <MessageIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Profile">
                      <IconButton 
                        sx={{ 
                          bgcolor: safeColors.orange[500],
                          color: safeColors.white[500],
                          '&:hover': { bgcolor: safeColors.orange[400] }
                        }}
                        onClick={()=>alert('Coming Soon !!!')}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    {(userRole === 'admin' || userRole === 'hr' || userRole === 'ceo' || userRole === 'coo' || userRole === 'supperAdmin') && (
                      <Tooltip title="Delete Staff">
                        <IconButton 
                          sx={{ 
                            bgcolor: safeColors.red[500],
                            color: safeColors.white[500],
                            '&:hover': { bgcolor: safeColors.red[400] }
                          }}
                          onClick={()=>alert('Coming Soon !!!')}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Quick Stats Cards */}
            <Grid container spacing={3} mb={3}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card sx={{ bgcolor: safeColors.primary[500], border: `1px solid ${safeColors.primary[400]}` }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="body2" color={safeColors.primary[100]} gutterBottom>
                          Total Leaves
                        </Typography>
                        <Typography variant="h4" color={safeColors.white[100]} fontWeight="bold">
                          {staffLeaves.length}
                        </Typography>
                      </Box>
                      <Box sx={{ p: 1.5, bgcolor: alpha(safeColors.orange[500], 0.1), borderRadius: 2 }}>
                        <EventAvailableIcon sx={{ color: safeColors.orange[500], fontSize: 30 }} />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card sx={{ bgcolor: safeColors.primary[500], border: `1px solid ${safeColors.primary[400]}` }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="body2" color={safeColors.gray[100]} gutterBottom>
                          Pending Leaves
                        </Typography>
                        <Typography variant="h4" color={safeColors.white[100]} fontWeight="bold">
                          {staffLeaves.filter(l => l.status === 0).length}
                        </Typography>
                      </Box>
                      <Box sx={{ p: 1.5, bgcolor: alpha(safeColors.yellow[500], 0.1), borderRadius: 2 }}>
                        <ScheduleIcon sx={{ color: safeColors.yellow[500], fontSize: 30 }} />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card sx={{ bgcolor: safeColors.primary[500], border: `1px solid ${safeColors.primary[400]}` }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="body2" color={safeColors.gray[100]} gutterBottom>
                          Borrowed Items
                        </Typography>
                        <Typography variant="h4" color={safeColors.white[100]} fontWeight="bold">
                          {staffBorrows.length}
                        </Typography>
                      </Box>
                      <Box sx={{ p: 1.5, bgcolor: alpha(safeColors.blue[500], 0.1), borderRadius: 2 }}>
                        <AssignmentIcon sx={{ color: safeColors.blue[500], fontSize: 30 }} />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card sx={{ bgcolor: safeColors.primary[500], border: `1px solid ${safeColors.primary[400]}` }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="body2" color={safeColors.gray[100]} gutterBottom>
                          Days Employed
                        </Typography>
                        <Typography variant="h4" color={safeColors.white[100]} fontWeight="bold">
                          {staff.createDate ? Math.floor((new Date() - new Date(staff.createDate)) / (1000 * 60 * 60 * 24)) : 0}
                        </Typography>
                      </Box>
                      <Box sx={{ p: 1.5, bgcolor: alpha(safeColors.green[500], 0.1), borderRadius: 2 }}>
                        <TimelineIcon sx={{ color: safeColors.green[500], fontSize: 30 }} />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Tabs Section */}
            <Paper 
              elevation={0}
              sx={{ 
                bgcolor: safeColors.primary[500],
                border: `1px solid ${safeColors.primary[400]}`,
                borderRadius: 3
              }}
            >
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange}
                sx={{
                  borderBottom: `1px solid ${safeColors.primary[400]}`,
                  '& .MuiTab-root': {
                    color: safeColors.gray[500],
                    '&.Mui-selected': {
                      color: safeColors.blue[500]
                    }
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: safeColors.blue[500]
                  }
                }}
              >
                <Tab icon={<PersonIcon />} label="Personal Info" />
                <Tab icon={<WorkIcon />} label="Employment" />
                <Tab icon={<EventAvailableIcon />} label="Leave History" />
                <Tab icon={<AssignmentIcon />} label="Borrow History" />
                <Tab icon={<TimelineIcon />} label="Activity" />
              </Tabs>

              {/* Personal Info Tab */}
              <TabPanel value={tabValue} index={0}>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <List>
                      <ListItem>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: alpha(safeColors.blue[500], 0.1) }}>
                            <EmailIcon sx={{ color: safeColors.blue[500] }} />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText 
                          primary={<Typography variant="body2" color={safeColors.gray[100]}>Email Address</Typography>}
                          secondary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body1" color={safeColors.white[200]}>
                                {staff.email || 'N/A'}
                              </Typography>
                              {staff.email_verified && (
                                <VerifiedIcon sx={{ color: safeColors.teal[500], fontSize: 16 }} />
                              )}
                            </Box>
                          }
                        />
                      </ListItem>

                      <ListItem>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: alpha(safeColors.green[500], 0.1) }}>
                            <PhoneIcon sx={{ color: safeColors.green[500] }} />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText 
                          primary={<Typography variant="body2" color={safeColors.gray[100]}>Phone Number</Typography>}
                          secondary={
                            <Typography variant="body1" color={safeColors.white[200]}>
                              {staff.Cell || 'N/A'}
                            </Typography>
                          }
                        />
                      </ListItem>

                      <ListItem>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: alpha(safeColors.orange[500], 0.1) }}>
                            <CakeIcon sx={{ color: safeColors.orange[500] }} />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText 
                          primary={<Typography variant="body2" color={safeColors.gray[100]}>Date of Birth</Typography>}
                          secondary={
                            <Typography variant="body1" color={safeColors.white[200]}>
                              {formatDate(staff.date_of_birth)}
                            </Typography>
                          }
                        />
                      </ListItem>

                      <ListItem>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: alpha(safeColors.purple[500], 0.1) }}>
                            <FlagIcon sx={{ color: safeColors.purple[500] }} />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText 
                          primary={<Typography variant="body2" color={safeColors.gray[100]}>Nationality</Typography>}
                          secondary={
                            <Typography variant="body1" color={safeColors.white[200]}>
                              {staff.nationality || 'N/A'}
                            </Typography>
                          }
                        />
                      </ListItem>
                    </List>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <List>
                      <ListItem>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: alpha(safeColors.red[500], 0.1) }}>
                            <BadgeIcon sx={{ color: safeColors.red[500] }} />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText 
                          primary={<Typography variant="body2" color={safeColors.gray[100]}>National ID</Typography>}
                          secondary={
                            <Typography variant="body1" color={safeColors.white[200]}>
                              {staff.national_id || 'N/A'}
                            </Typography>
                          }
                        />
                      </ListItem>

                      <ListItem>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: alpha(safeColors.teal[500], 0.1) }}>
                            <LocationOnIcon sx={{ color: safeColors.teal[500] }} />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText 
                          primary={<Typography variant="body2" color={safeColors.gray[100]}>Physical Address</Typography>}
                          secondary={
                            <Typography variant="body1" color={safeColors.white[200]}>
                              {staff.physicalAddress || 'N/A'}
                            </Typography>
                          }
                        />
                      </ListItem>

                      <ListItem>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: alpha(safeColors.indigo[500], 0.1) }}>
                            <LocationOnIcon sx={{ color: safeColors.indigo[500] }} />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText 
                          primary={<Typography variant="body2" color={safeColors.gray[100]}>Postal Address</Typography>}
                          secondary={
                            <Typography variant="body1" color={safeColors.white[200]}>
                              {staff.postalAddress || 'N/A'}
                            </Typography>
                          }
                        />
                      </ListItem>

                      <ListItem>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: alpha(safeColors.yellow[500], 0.1) }}>
                            <FavoriteIcon sx={{ color: safeColors.yellow[500] }} />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText 
                          primary={<Typography variant="body2" color={safeColors.gray[100]}>Next of Kin</Typography>}
                          secondary={
                            <Typography variant="body1" color={safeColors.white[200]}>
                              {staff.next_of_kin || 'N/A'}
                            </Typography>
                          }
                        />
                      </ListItem>
                    </List>
                  </Grid>
                </Grid>
              </TabPanel>

              {/* Employment Tab */}
              <TabPanel value={tabValue} index={1}>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <List>
                      <ListItem>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: alpha(safeColors.blue[500], 0.1) }}>
                            <WorkIcon sx={{ color: safeColors.blue[500] }} />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText 
                          primary={<Typography variant="body2" color={safeColors.gray[100]}>Role</Typography>}
                          secondary={
                            <Typography variant="body1" color={safeColors.white[200]}>
                              {staff.role || 'N/A'}
                            </Typography>
                          }
                        />
                      </ListItem>

                      <ListItem>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: alpha(safeColors.green[500], 0.1) }}>
                            <SchoolIcon sx={{ color: safeColors.green[500] }} />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText 
                          primary={<Typography variant="body2" color={safeColors.gray[100]}>Department</Typography>}
                          secondary={
                            <Typography variant="body1" color={safeColors.white[200]}>
                              {staff.department || 'N/A'}
                            </Typography>
                          }
                        />
                      </ListItem>

                      <ListItem>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: alpha(safeColors.orange[500], 0.1) }}>
                            <TodayIcon sx={{ color: safeColors.orange[500] }} />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText 
                          primary={<Typography variant="body2" color={safeColors.gray[100]}>Start Date</Typography>}
                          secondary={
                            <Typography variant="body1" color={safeColors.white[200]}>
                              {formatDate(staff.createDate)}
                            </Typography>
                          }
                        />
                      </ListItem>
                    </List>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <List>
                      <ListItem>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: alpha(safeColors.purple[500], 0.1) }}>
                            <SecurityIcon sx={{ color: safeColors.purple[500] }} />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText 
                          primary={<Typography variant="body2" color={safeColors.gray[100]}>Role Explanation</Typography>}
                          secondary={
                            <Typography variant="body1" color={safeColors.white[200]}>
                              {staff.role_explanation || 'No explanation provided'}
                            </Typography>
                          }
                        />
                      </ListItem>

                      <ListItem>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: alpha(safeColors.red[500], 0.1) }}>
                            <BadgeIcon sx={{ color: safeColors.red[500] }} />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText 
                          primary={<Typography variant="body2" color={safeColors.gray[100]}>Employment Status</Typography>}
                          secondary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body1" color={safeColors.white[200]}>
                                {getStatusText()}
                              </Typography>
                              <Chip 
                                label={getStatusText()}
                                size="small"
                                sx={{ 
                                  bgcolor: alpha(getStatusColor(), 0.1),
                                  color: getStatusColor()
                                }}
                              />
                            </Box>
                          }
                        />
                      </ListItem>
                    </List>
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <Typography variant="h6" color={safeColors.white[100]} gutterBottom sx={{ mt: 2 }}>
                      About Staff
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <Paper sx={{ p: 2, bgcolor: safeColors.primary[400] }}>
                          <Typography variant="body2" color={safeColors.gray[500]} gutterBottom>
                            Drive Quote
                          </Typography>
                          <Typography variant="body1" color={safeColors.white[500]}>
                            {staff.drive_quote || 'N/A'}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <Paper sx={{ p: 2, bgcolor: safeColors.primary[400] }}>
                          <Typography variant="body2" color={safeColors.gray[500]} gutterBottom>
                            Passion
                          </Typography>
                          <Typography variant="body1" color={safeColors.white[500]}>
                            {staff.passion || 'N/A'}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <Paper sx={{ p: 2, bgcolor: safeColors.primary[400] }}>
                          <Typography variant="body2" color={safeColors.gray[500]} gutterBottom>
                            Knowledge
                          </Typography>
                          <Typography variant="body1" color={safeColors.white[500]}>
                            {staff.knowledge || 'N/A'}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <Paper sx={{ p: 2, bgcolor: safeColors.primary[400] }}>
                          <Typography variant="body2" color={safeColors.gray[500]} gutterBottom>
                            Core Values
                          </Typography>
                          <Typography variant="body1" color={safeColors.white[500]}>
                            {staff.core_values || 'N/A'}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </TabPanel>

              {/* Leave History Tab */}
              <TabPanel value={tabValue} index={2}>
                {staffLeaves.length > 0 ? (
                  <List>
                    {staffLeaves.map((leave, index) => (
                      <Box key={leave.id}>
                        <ListItem>
                          <ListItemAvatar>
                            <Avatar sx={{ 
                              bgcolor: leave.status === 4 ? alpha(safeColors.green[500], 0.1) :
                                      leave.status === 0 ? alpha(safeColors.orange[500], 0.1) :
                                      alpha(safeColors.red[500], 0.1)
                            }}>
                              {leave.status === 4 ? <EventAvailableIcon sx={{ color: safeColors.green[500] }} /> :
                               leave.status === 0 ? <ScheduleIcon sx={{ color: safeColors.orange[500] }} /> :
                               <EventBusyIcon sx={{ color: safeColors.red[500] }} />}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText 
                            primary={
                              <Typography variant="body1" color={safeColors.white[100]}>
                                {leave.leave_type} Leave
                              </Typography>
                            }
                            secondary={
                              <Box>
                                <Typography variant="caption" color={safeColors.gray[400]} component="span">
                                  {formatDate(leave.startDate)} - {formatDate(leave.endDate)}
                                </Typography>
                                <Typography variant="body2" color={safeColors.gray[400]}>
                                  Reason: {leave.reason}
                                </Typography>
                              </Box>
                            }
                          />
                          <Chip 
                            label={
                              leave.status === 4 ? 'Approved' :
                              leave.status === 0 ? 'Pending' :
                              'Rejected'
                            }
                            size="small"
                            sx={{ 
                              bgcolor: leave.status === 4 ? alpha(safeColors.green[500], 0.1) :
                                      leave.status === 0 ? alpha(safeColors.orange[500], 0.1) :
                                      alpha(safeColors.red[500], 0.1),
                              color: leave.status === 4 ? safeColors.green[500] :
                                     leave.status === 0 ? safeColors.orange[500] :
                                     safeColors.red[500]
                            }}
                          />
                        </ListItem>
                        {index < staffLeaves.length - 1 && <Divider sx={{ bgcolor: safeColors.primary[400] }} />}
                      </Box>
                    ))}
                  </List>
                ) : (
                  <Box textAlign="center" py={4}>
                    <Typography color={safeColors.gray[500]}>No leave records found</Typography>
                  </Box>
                )}
              </TabPanel>

              {/* Borrow History Tab */}
              <TabPanel value={tabValue} index={3}>
                {staffBorrows.length > 0 ? (
                  <List>
                    {staffBorrows.map((borrow, index) => (
                      <Box key={borrow.id}>
                        <ListItem>
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: alpha(safeColors.blue[500], 0.1) }}>
                              <AssignmentIcon sx={{ color: safeColors.blue[500] }} />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText 
                            primary={
                              <Typography variant="body1" color={safeColors.white[100]}>
                                {borrow.equipment}
                              </Typography>
                            }
                            secondary={
                              <Box>
                                <Typography variant="caption" color={safeColors.gray[400]} component="span">
                                  Borrowed on: {formatDate(borrow.date)}
                                </Typography>
                                <Typography variant="body2" color={safeColors.gray[400]}>
                                  Duration: {borrow.duration} | Destination: {borrow.destination}
                                </Typography>
                              </Box>
                            }
                          />
                          <Chip 
                            label={borrow.status === 1 ? 'Returned' : 'Pending'}
                            size="small"
                            sx={{ 
                              bgcolor: borrow.status === 1 ? alpha(safeColors.green[500], 0.1) : alpha(safeColors.orange[500], 0.1),
                              color: borrow.status === 1 ? safeColors.green[500] : safeColors.orange[500]
                            }}
                          />
                        </ListItem>
                        {index < staffBorrows.length - 1 && <Divider sx={{ bgcolor: safeColors.primary[100] }} />}
                      </Box>
                    ))}
                  </List>
                ) : (
                  <Box textAlign="center" py={4}>
                    <Typography color={safeColors.gray[400]}>No borrow records found</Typography>
                  </Box>
                )}
              </TabPanel>

              {/* Activity Tab */}
              <TabPanel value={tabValue} index={4}>
                {staffActivity.length > 0 ? (
                  <List>
                    {staffActivity.map((activity, index) => (
                      <Box key={activity.id}>
                        <ListItem>
                          <ListItemAvatar>
                            <Avatar sx={{ 
                              bgcolor: activity.type === 'auth' ? alpha(safeColors.blue[500], 0.1) :
                                      activity.type === 'profile' ? alpha(safeColors.green[500], 0.1) :
                                      alpha(safeColors.orange[500], 0.1)
                            }}>
                              {activity.type === 'auth' ? <LoginIcon sx={{ color: safeColors.blue[500] }} /> :
                               activity.type === 'profile' ? <PersonIcon sx={{ color: safeColors.green[500] }} /> :
                               <EventAvailableIcon sx={{ color: safeColors.orange[500] }} />}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText 
                            primary={
                              <Typography variant="body1" color={safeColors.white[100]}>
                                {activity.action}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" color={safeColors.gray[400]}>
                                {new Date(activity.date).toLocaleString()}
                              </Typography>
                            }
                          />
                        </ListItem>
                        {index < staffActivity.length - 1 && <Divider sx={{ bgcolor: safeColors.primary[100] }} />}
                      </Box>
                    ))}
                  </List>
                ) : (
                  <Box textAlign="center" py={4}>
                    <Typography color={safeColors.gray[400]}>No activity records found</Typography>
                  </Box>
                )}
              </TabPanel>
            </Paper>
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default StaffProfile;